/**
 * §M1/§M4 · "Mis perfiles" — the screen that makes profiles obvious.
 *
 * One academy per card: **what I am**, **what I can do**, **what I can't** and
 * **why**. Everything derives from the RBAC contract, the `nav-model` and the
 * feature manifest (see `profile-model.ts`); there isn't a single
 * hand-written list here — if there were, it would drift from the real system on the
 * first new feature.
 *
 * The distinction that gives the screen its value: "you're missing a role" and "your
 * academy turned it off" feel the same (you can't get in) but get fixed the opposite
 * way (asking for a promotion vs. turning on a feature), so they're shown differently and
 * named differently.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { msg } from "@lingui/core/macro";
import { CircleHelp, IdCard, Lock, ToggleLeft } from "@fleet/icons";
import { featureGraph } from "@space/shared";

import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";
import { Badge, PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { useSession } from "@/lib/auth";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { helpSummaryFor } from "@/features/help/registry";
import type { NavItem } from "@/features/navigation/nav-model";
import { NAV_SECTIONS } from "@/features/navigation/nav-model";
import {
  capabilitiesForRole,
  featureLabel,
  offeredHelpFor,
  type ScreenAccess,
  screensForContext,
} from "./profile-model";
import { useOrgNavContext } from "./use-org-nav-context";

/** Organization role's readable label (not invented: maps the contract). */
const ROLE_LABELS = {
  owner: msg`Owner`,
  org_admin: msg`Academy admin`,
  campus_admin: msg`Campus admin`,
  staff: msg`Staff`,
  teacher: msg`Teacher`,
  guardian: msg`Guardian`,
  student: msg`Student`,
} as const;

/** Nav item's icon, so the screen is recognizable at a glance. */
function iconFor(testid: string): NavItem["icon"] | undefined {
  return NAV_SECTIONS.flatMap((s) => s.items).find((i) => i.testid === testid)?.icon;
}

/** A screen's row, with its explanation and its link to the guide. */
const ScreenRow: Component<{ screen: ScreenAccess }> = (props) => {
  const { t } = useI18n();
  const graph = featureGraph();
  const summary = () => {
    const { help } = props.screen;
    return help ? helpSummaryFor(help) : undefined;
  };
  const reasonText = () => {
    if (props.screen.allowed) {
      return undefined;
    }
    if (props.screen.reason === "role") {
      return t(
        msg`Your role in this academy doesn't reach this screen. Only an academy admin can change your role.`,
      );
    }
    const blocked = props.screen.blockedBy ?? props.screen.feature ?? "";
    const name = featureLabel(blocked, graph);
    return blocked === props.screen.feature
      ? t(
          msg`Your academy turned "${name}" off. Your data is kept and comes back the moment it's turned on again.`,
        )
      : t(
          msg`This needs "${name}", which your academy turned off. Your data is kept and comes back the moment it's turned on again.`,
        );
  };

  return (
    <li
      class="flex items-start gap-3 rounded-xl border border-border/50 bg-surface/30 px-3 py-2.5"
      data-testid={`profile-screen-${props.screen.testid}`}
      data-allowed={props.screen.allowed ? "yes" : "no"}
      data-reason={props.screen.reason ?? "granted"}
    >
      <span
        class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        classList={{
          "bg-primary/10 text-primary": props.screen.allowed,
          "bg-muted/40 text-muted-foreground": !props.screen.allowed,
        }}
      >
        <Show when={iconFor(props.screen.testid)} fallback={<IdCard class="h-4 w-4" />}>
          {(icon) => <Dynamic component={icon()} class="h-4 w-4" />}
        </Show>
      </span>
      <div class="min-w-0 flex-1 space-y-1">
        <div class="flex flex-wrap items-center gap-2">
          <Show
            when={props.screen.allowed}
            fallback={<span class="text-sm font-semibold">{t(props.screen.label)}</span>}
          >
            <Link to={props.screen.to} class="text-sm font-semibold text-primary hover:underline">
              {t(props.screen.label)}
            </Link>
          </Show>
          <Show when={!props.screen.allowed && props.screen.reason === "role"}>
            <Badge variant="outline" class="gap-1 text-[11px]">
              <Lock class="h-3 w-3" /> {t(msg`Needs another role`)}
            </Badge>
          </Show>
          <Show when={!props.screen.allowed && props.screen.reason === "feature"}>
            <Badge variant="outline" class="gap-1 text-[11px]">
              <ToggleLeft class="h-3 w-3" /> {t(msg`Turned off by your academy`)}
            </Badge>
          </Show>
        </div>
        {/* §M4: the explanatory text is the SAME as the guide's, not a copy. */}
        <Show when={summary()}>
          {(s) => <p class="text-xs leading-relaxed text-muted-foreground">{t(s())}</p>}
        </Show>
        <Show when={reasonText()}>
          {(reason) => (
            <p class="text-xs leading-relaxed text-muted-foreground/90 italic">{reason()}</p>
          )}
        </Show>
        {/* §M2: documentation is only offered for what you CAN use. */}
        <Show when={offeredHelpFor(props.screen)}>
          {(help) => (
            <Link
              to="/help/$guideSlug"
              params={{ guideSlug: help().guide }}
              search={{ section: help().section }}
              data-testid={`profile-help-${props.screen.testid}`}
              class="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
            >
              <CircleHelp class="h-3.5 w-3.5" /> {t(msg`Read the guide`)}
            </Link>
          )}
        </Show>
      </div>
    </li>
  );
};

/** ONE academy's card: role, contract capabilities and screens. */
const OrgProfileCard: Component<{ orgId: string; name: string }> = (props) => {
  const { t } = useI18n();
  const { context, blockerOf } = useOrgNavContext(() => props.orgId);
  const screens = createMemo(() => screensForContext(context(), blockerOf));
  const allowed = () => screens().filter((s) => s.allowed);
  const denied = () => screens().filter((s) => !s.allowed);
  const role = () => context().orgRole;
  const capabilities = createMemo(() => capabilitiesForRole(role()));

  return (
    <section
      class="space-y-4 rounded-2xl border-2 border-border bg-surface/40 p-4 md:p-5"
      data-testid={`profile-org-${props.orgId}`}
    >
      <header class="flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-lg font-bold tracking-tight">{props.name}</h2>
        <Badge data-testid={`profile-role-${props.orgId}`}>
          {t(ROLE_LABELS[role() ?? "student"])}
        </Badge>
      </header>

      <div class="space-y-1.5">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {t(msg`What this profile lets you do`)}
        </h3>
        <ul class="grid gap-1.5 sm:grid-cols-2">
          <For each={capabilities()}>
            {(cap) => (
              <li
                class="rounded-lg border border-border/40 px-2.5 py-1.5 text-xs"
                data-testid={`profile-capability-${cap.capability}`}
                data-granted={cap.granted ? "yes" : "no"}
                classList={{ "opacity-55": !cap.granted }}
              >
                <span class="font-semibold">
                  {cap.granted ? "✓" : "—"} {t(cap.label)}
                </span>
                <span class="block text-muted-foreground">{t(cap.description)}</span>
              </li>
            )}
          </For>
        </ul>
      </div>

      <div class="space-y-1.5">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {t(msg`Screens available to you`)}
        </h3>
        <ul class="space-y-1.5">
          <For each={allowed()}>{(screen) => <ScreenRow screen={screen} />}</For>
        </ul>
      </div>

      <Show when={denied().length > 0}>
        <div class="space-y-1.5">
          <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t(msg`Not available — and why`)}
          </h3>
          <ul class="space-y-1.5">
            <For each={denied()}>{(screen) => <ScreenRow screen={screen} />}</For>
          </ul>
        </div>
      </Show>
    </section>
  );
};

export const ProfilesView: Component = () => {
  const { t } = useI18n();
  const session = useSession();
  const { orgs, isLoading } = useActiveOrg();

  return (
    <PageContainer maxWidth="4xl">
      <div class="space-y-6 p-4 md:p-6">
        <PageHeader
          icon={IdCard}
          title={t(msg`My profiles`)}
          description={t(
            msg`You can belong to several academies with a different role in each. Here's what you are in every one of them, what it lets you do, and why the rest is out of reach.`,
          )}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <EmptyState
              icon={IdCard}
              title={t(msg`Sign in to see your profiles`)}
              description={t(
                msg`Profiles describe what you are inside an academy. As a guest you're not a member of any yet.`,
              )}
            />
          }
        >
          <Show when={!isLoading()} fallback={<Skeleton class="h-64 w-full rounded-2xl" />}>
            <Show
              when={orgs().length > 0}
              fallback={
                <EmptyState
                  icon={IdCard}
                  title={t(msg`You don't belong to any academy yet`)}
                  description={t(
                    msg`When an academy invites you — or you enroll in one of its cohorts — your profile there will show up here with everything it unlocks.`,
                  )}
                />
              }
            >
              <div class="space-y-4" data-testid="profiles-list">
                <For each={orgs()}>
                  {(org) => <OrgProfileCard orgId={org.id} name={org.name ?? org.id} />}
                </For>
              </div>
            </Show>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};
